"use client";

import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { RANGES, type HistoryRange } from "@/lib/app-detail/daily-buckets";

export interface RangeToggleOption<T extends string> {
	value: T;
	label: ReactNode;
}

const HISTORY_RANGE_OPTIONS: readonly RangeToggleOption<HistoryRange>[] = RANGES.map((r) => ({ value: r, label: r }));

/** A row of small buttons, one pressed. Used in a ChartCard `action` slot. */
export function RangeToggle<T extends string = HistoryRange>({
	value,
	onChange,
	options = HISTORY_RANGE_OPTIONS as unknown as readonly RangeToggleOption<T>[],
	label = "Range",
	className,
}: {
	value: T;
	onChange: (value: T) => void;
	options?: readonly RangeToggleOption<T>[];
	/** The group's accessible name; a card with two of these names the second. */
	label?: string;
	className?: string;
}) {
	return (
		<div role="group" aria-label={label} className={cn("inline-flex items-center gap-1", className)}>
			{options.map((o) => (
				<Button
					key={o.value}
					type="button"
					size="xs"
					variant={o.value === value ? "default" : "outline"}
					aria-pressed={o.value === value}
					onClick={() => onChange(o.value)}
				>
					{o.label}
				</Button>
			))}
		</div>
	);
}
